"use client";

import { hasAnalyticsConsent, initializeConsent, waitForConsentSync } from './consent';
import { biteCampaign, clearBiteCampaign } from './bite-campaign';
import { biteAnalyticsEntry } from './bite-entry';
import {biteMeasurementPath} from './bite-measurement-path';
import {countBiteUsage} from './bite-usage-totals';
import {countBiteStage} from './bite-conversion';
import {trackBiteGa4,biteGa4CheckoutContext} from './bite-ga4';
import {biteGa4PolicyContext} from './bite-ga4-policy';
import {captureBiteSignupReferral,clearBiteSignupReferral} from './bite-signup-referral';
import {
  importBiteHandoff, getSharedVisitorId, getStoredSource, getStoredEntryPath,
  getOrCreatePageMeasurementIdentity, createPageMeasurementAttribution, createPageviewId,
  setActivePageMeasurementIdentity, bindMeasurementActivityListeners,
  createMeasurementActivityTouch, touchMeasurementSession, clearMeasurementIdentity, getMeasurementSessionAttribution,
  type PageMeasurementIdentity, type CrossProductOrigin,
} from './bullstory-measurement/source';

const ORIGIN_KEY = 'bite_measurement_origin';
const QA_KEY = 'bite_measurement_test';
const PROMO_KEY = 'bite_promotion_click';
let page: PageMeasurementIdentity | null = null;
let pageRoute = '';
let documentEntry = true;
let pending: Record<string, unknown>[] = [];
let sending = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

export function isBiteMeasurementPath(path: string) {
  return path === '/bite' || path.startsWith('/bite/') || (path === '/' && /^(www\.)?bite\.cx$/.test(window.location.hostname));
}

function readOrigin(): CrossProductOrigin | null {
  try { return JSON.parse(sessionStorage.getItem(ORIGIN_KEY) || 'null'); } catch { return null; }
}

function promotionClick() {
  const fragment = new URLSearchParams(location.hash.slice(1));
  const token = fragment.get('bs_promo');
  if (token) {
    if (/^[a-f0-9]{64}$/.test(token)) localStorage.setItem(PROMO_KEY, JSON.stringify({ token, at: Date.now() }));
    fragment.delete('bs_promo');
    history.replaceState(history.state, '', location.pathname + location.search + (fragment.size ? `#${fragment}` : ''));
  }
  const saved = JSON.parse(localStorage.getItem(PROMO_KEY) || 'null');
  if (saved && /^[a-f0-9]{64}$/.test(saved.token) && Date.now() - saved.at < 30 * 86400000) return saved.token as string;
  localStorage.removeItem(PROMO_KEY);
  return '';
}

export function biteMeasurementContext() {
  try {
  if (!hasAnalyticsConsent() || !page) return null;
  const origin = readOrigin();
  const entry = biteAnalyticsEntry();
  return { measurementMode:'account-v2', visitorId: getSharedVisitorId(), ...createPageMeasurementAttribution(page, getStoredSource(entry?.source), getStoredEntryPath(entry?.path)),
    originEventId: origin?.eventId || '', promotionClick: promotionClick(), campaign: biteCampaign(),
    isTest: navigator.webdriver || localStorage.getItem(QA_KEY) === 'true' || /^(localhost|127\.0\.0\.1)$/.test(location.hostname) };
  } catch { return null; }
}

export function trackBiteEvent(type: string, metadata: Record<string, unknown> = {}) {
  countBiteUsage(type);
  void trackBiteGa4(type,metadata);
  if(type==='click'&&metadata.label==='bite.report_prices.subscribe')void countBiteStage('paywall',{...metadata,label:'report_chart'});
  if(type==='click'&&metadata.eligible===true&&['bite.report_gift.open','bite.report_gift.close','bite.landing_gift.open','bite.landing_gift.close'].includes(String(metadata.label))) {
    const label=String(metadata.label);
    void countBiteStage(label.endsWith('.open')?'report_gift_open':'report_gift_close',{...metadata,label:label.startsWith('bite.landing_')?'pricing':'report_offer'});
  }
  const stage:Record<string,string>={search:'search',login_click:'login_start',paywall_view:'paywall',checkout_click:'checkout_click',checkout_request:'checkout_request',checkout_redirect:'checkout_redirect',checkout_error:'checkout_error',report_open:'report_open',login_success:'login_success',login_failed:'login_failed',login_dismiss:'login_dismiss',login_google:'login_google',login_apple:'login_apple'};
  if(stage[type]&&!(type==='report_open'&&location.pathname.startsWith('/bite/examples/')))countBiteStage(stage[type],metadata);
  if(['checkout_request','checkout_error','login_success','login_failed','login_dismiss','login_google','login_apple'].includes(type))return;
  trackIdentifiedBiteEvent(type,metadata);
}

const screenRecords=new Map<string,{counted:boolean;identified:boolean;google?:boolean}>();
export function trackBiteScreen(type:string,identity:string,metadata:Record<string,unknown>={}) {
  if(!isBiteMeasurementPath(location.pathname))return;
  const key=JSON.stringify([location.pathname,type,identity]);
  const record=screenRecords.get(key)||{counted:false,identified:false};
  if(!record.google){
    record.google=true;
    void trackBiteGa4(type,metadata).then(sent=>{if(!sent)record.google=false;}).catch(()=>{record.google=false;});
  }
  if(!record.counted) {
    record.counted=true;
    const stage=['completion_view','report_open','report_gift_view','report_offer_view','login_view'].includes(type)?type:null;
    if(stage&&!(stage==='report_open'&&location.pathname.startsWith('/bite/examples/')))void countBiteStage(stage,metadata);
    if(type==='report_open'||type==='generation_ready'||type==='click')countBiteUsage(type);
  }
  if(hasAnalyticsConsent()&&!record.identified&&biteMeasurementContext()) {
    record.identified=true;
    if(type==='report_open'||type==='generation_ready'||type==='click')trackIdentifiedBiteEvent(type,metadata);
  }
  screenRecords.set(key,record);
  if(screenRecords.size>128)screenRecords.delete(screenRecords.keys().next().value!);
}

function trackIdentifiedBiteEvent(type: string, metadata: Record<string, unknown> = {}) {
  const context = biteMeasurementContext();
  if (!context) return;
  const data: Record<string, unknown> = {};
  for (const key of ['ticker', 'label', 'plan', 'ms', 'scroll', 'engagement']) if (metadata[key] !== undefined) data[key] = metadata[key];
  pending.push({ ...data, ...context, type, eventId: createPageviewId(), occurredAt: new Date().toISOString() });
  if(pending.length>100)pending.splice(0,pending.length-100);
  if (!timer) timer = setTimeout(() => { timer = null; void flushBiteMeasurement(); }, 250);
}

export async function flushBiteMeasurement(urgent=false) {
  if (!hasAnalyticsConsent()) { pending = []; return; }
  if ((sending&&!urgent) || !pending.length) return;
  sending++;
  const batch = pending.splice(0, 20);
  try {
    const response = await fetch('/api/analytics/event', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ measurement: 'bullstory-v1', consent: true, events: batch }), keepalive: true });
    if (!response.ok) throw new Error('measurement_not_accepted');
  } catch { pending.unshift(...batch); }
  finally {
    sending--;
    if (pending.length && !timer) timer = setTimeout(() => { timer = null; void flushBiteMeasurement(); }, 3000);
  }
}

function identifyBitePage(pathname: string) {
  if (!hasAnalyticsConsent()) return;
  const imported = importBiteHandoff();
  if (imported) sessionStorage.setItem(ORIGIN_KEY, JSON.stringify(imported));
  const path = biteMeasurementPath(pathname === '/' ? '/bite' : pathname);
  if (!path) return;
  const previous = page;
  if(pageRoute!==pathname)page=null;
  let returning = false;
  try { returning = ['accounts.google.com', 'checkout.stripe.com', 'billing.stripe.com'].includes(new URL(document.referrer).hostname); } catch { /* no referrer */ }
  const entry = biteAnalyticsEntry();
  page = getOrCreatePageMeasurementIdentity(page, path, {
    isDocumentEntry: documentEntry && !imported && !returning && !readOrigin(),
    getSession: (currentPath, isEntry) => getMeasurementSessionAttribution(
      isEntry && entry?.path ? entry.path : currentPath, isEntry, Date.now(), entry?.source),
  });
  documentEntry = false;
  setActivePageMeasurementIdentity(page);
  if (!page) return;
  pageRoute=pathname;
  if (readOrigin()?.sessionId !== page.sessionId) sessionStorage.removeItem(ORIGIN_KEY);
  if (page !== previous) trackIdentifiedBiteEvent('pageview');
  if (page !== previous && path === '/bite/collection') trackIdentifiedBiteEvent('collection_view');
  if (page !== previous && path === '/bite/me') trackIdentifiedBiteEvent('account_view');
  if (page !== previous && new URLSearchParams(location.search).get('payment') === 'cancelled') trackIdentifiedBiteEvent('checkout_cancel');
  if(page!==previous)window.dispatchEvent(new Event('bite:measurement-ready'));
}

function beginBitePage(pathname: string) {
  void captureBiteSignupReferral();
  identifyBitePage(pathname);
  if (!hasAnalyticsConsent() || !page) return () => {};
  const pageAtStart = page;
  const unbind = bindMeasurementActivityListeners(window, createMeasurementActivityTouch((at) => touchMeasurementSession(pageAtStart.sessionId, at)));
  let visibleSince = document.visibilityState === 'visible' ? Date.now() : 0;
  let activeMs = 0, scroll = 0;
  const updateScroll = () => { scroll = Math.max(scroll, Math.round((window.scrollY + innerHeight) / Math.max(document.documentElement.scrollHeight, innerHeight) * 100)); };
  const publish = () => {
    if (visibleSince) { activeMs += Date.now() - visibleSince; visibleSince = 0; }
    if (page === pageAtStart) trackIdentifiedBiteEvent('dwell', { ms: activeMs, scroll });
    void flushBiteMeasurement();
  };
  const visibility = () => { if (document.visibilityState === 'hidden') publish(); else visibleSince = Date.now(); };
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('pagehide', publish);
  window.addEventListener('scroll', updateScroll, { passive: true });
  return () => { publish(); unbind(); document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('pagehide', publish); window.removeEventListener('scroll', updateScroll); };
}

export function startBitePage(pathname: string) {
  try { return beginBitePage(pathname); } catch { return () => {}; }
}

export async function prepareBiteCheckoutMeasurement() {
  await initializeConsent();
  await waitForConsentSync();
  await captureBiteSignupReferral();
  try {
    identifyBitePage(location.pathname);
    let ga4=null;
    try{ga4=await biteGa4CheckoutContext();}catch{/* Preserve first-party attribution if the Google tag fails. */}
    const context=biteMeasurementContext();
    return context?{...context,...(ga4?{ga4}:{})}:ga4?{measurementMode:'ga4-only',ga4,...biteGa4PolicyContext()}:null;
  } catch { return null; }
}

export function resetBiteMeasurement() {
  clearBiteSignupReferral();
  screenRecords.clear();
  pending = []; page = null; pageRoute=''; documentEntry = true; clearMeasurementIdentity();
  if (timer) clearTimeout(timer);
  timer = null;
  try { sessionStorage.removeItem(ORIGIN_KEY); localStorage.removeItem(PROMO_KEY); clearBiteCampaign(); } catch { /* storage denied */ }
  const hash = new URLSearchParams(location.hash.slice(1));
  if (hash.has('bs_handoff') || hash.has('bs_promo')) { hash.delete('bs_handoff'); hash.delete('bs_promo'); history.replaceState(history.state, '', location.pathname + location.search + (hash.size ? `#${hash}` : '')); }
}
